import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { BASE_URL } from "../utilities/BASE_URL";
import { fetchWithToken, postWithToken } from "../utilities/fetchWithToken";
import { getMasterVolume, setMasterVolume, getMuted, setMuted } from "../utilities/sounds";
import { setToken } from "../redux/authSlice";
import { getBetSettings, saveBetSettings } from "../redux/playerInfoSlice";
import "./Settings.css";

const TABS = ["Account", "Volume", "Rates Slider", "Support"];

const Settings = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("Account");

    // Account
    const [email, setEmail] = useState("");
    const [emailStatus, setEmailStatus] = useState(null); // { type: "ok" | "error", text }
    const [savingEmail, setSavingEmail] = useState(false);

    // Volume (slider is 0..100; master volume is 0..1)
    const [volume, setVolume] = useState(Math.round(getMasterVolume() * 100));
    const [muted, setMutedState] = useState(getMuted());
    const [volumeSaved, setVolumeSaved] = useState(false);

    // Rates Slider — quick-select bet presets. Held as strings so the inputs edit
    // freely; parsed to numbers on save. Seeded from the store (defaults until loaded).
    const betSettings = useSelector((state) => state.playerInfo.betSettings);
    const [preflop, setPreflop] = useState(betSettings.preflop.map(String));
    const [postflop, setPostflop] = useState(betSettings.postflop.map(String));
    const [ratesStatus, setRatesStatus] = useState(null);

    // Pull the saved presets, then mirror them into the editable inputs once they land.
    useEffect(() => { dispatch(getBetSettings()); }, [dispatch]);
    useEffect(() => {
        setPreflop(betSettings.preflop.map(String));
        setPostflop(betSettings.postflop.map(String));
    }, [betSettings]);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            navigate("/");
            return;
        }
        // Load the current login email from the token's subject.
        fetchWithToken(`${BASE_URL}/autha/validate`)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (data && data.email) setEmail(data.email);
            })
            .catch(() => {});
    }, [navigate]);

    const handleChangeEmail = async () => {
        setEmailStatus(null);
        setSavingEmail(true);
        try {
            const res = await postWithToken(`${BASE_URL}/autha/changeEmail`, { email: email.trim() });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || "Failed to change email");
            }
            dispatch(setToken(data.token)); // old token was bound to the old email
            setEmailStatus({ type: "ok", text: "Email updated." });
        } catch (err) {
            setEmailStatus({ type: "error", text: err.message });
        } finally {
            setSavingEmail(false);
        }
    };

    const handleSaveVolume = () => {
        setMasterVolume(volume / 100);
        setMuted(muted);
        setVolumeSaved(true);
    };

    const handleRateChange = (setter, index, value) => {
        setter((prev) => prev.map((v, i) => (i === index ? value : v)));
        setRatesStatus(null);
    };

    const handleSaveRates = async () => {
        setRatesStatus(null);
        const pre = preflop.map(Number);
        const post = postflop.map(Number);
        const valid = [...pre, ...post].every((n) => !isNaN(n) && n > 0 && n <= 1000);
        if (!valid) {
            setRatesStatus({ type: "error", text: "Enter numbers greater than 0 and at most 1000." });
            return;
        }
        try {
            await dispatch(saveBetSettings({ preflop: pre, postflop: post })).unwrap();
            setRatesStatus({ type: "ok", text: "Bet presets saved." });
        } catch (err) {
            setRatesStatus({ type: "error", text: typeof err === "string" ? err : "Failed to save presets." });
        }
    };

    const renderAccount = () => (
        <div className="settings-panel">
            <div className="settings-panel-header">
                <h2>Email</h2>
            </div>
            <p className="settings-panel-desc">
                Your Email will be used for all official communication and account security purposes.
            </p>
            <hr className="settings-divider" />
            <label className="settings-field-label" htmlFor="settings-email">Email</label>
            <input
                id="settings-email"
                className="settings-input"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailStatus(null); }}
            />
            {emailStatus && (
                <p className={emailStatus.type === "ok" ? "settings-msg-ok" : "settings-msg-error"}>
                    {emailStatus.text}
                </p>
            )}
            <div className="settings-actions settings-actions-right">
                <button
                    className="settings-action-btn"
                    onClick={handleChangeEmail}
                    disabled={savingEmail || !email.trim()}
                >
                    {savingEmail ? "Saving..." : "Change Email"}
                </button>
            </div>
        </div>
    );

    const renderVolume = () => (
        <div className="settings-panel">
            <div className="settings-panel-header">
                <h2>Volume</h2>
            </div>
            <p className="settings-panel-desc">
                Adjust the master volume for all in-game sounds.
            </p>
            <hr className="settings-divider" />
            <label className="settings-field-label" htmlFor="settings-volume">
                Master Volume — {muted ? "Muted" : `${volume}%`}
            </label>
            <input
                id="settings-volume"
                className="settings-slider"
                type="range"
                min="0"
                max="100"
                value={volume}
                disabled={muted}
                style={{ "--fill": `${volume}%` }}
                onChange={(e) => { setVolume(Number(e.target.value)); setVolumeSaved(false); }}
            />
            <label className="settings-checkbox">
                <input
                    type="checkbox"
                    checked={muted}
                    onChange={(e) => { setMutedState(e.target.checked); setVolumeSaved(false); }}
                />
                Mute
            </label>
            {volumeSaved && <p className="settings-msg-ok">Volume saved.</p>}
            <div className="settings-actions settings-actions-left">
                <button className="settings-action-btn" onClick={handleSaveVolume}>
                    Save
                </button>
            </div>
        </div>
    );

    const renderRateRow = (values, setter, suffix, step) => (
        <div className="rates-grid">
            {values.map((v, i) => (
                <div className="rates-cell" key={i}>
                    <span className="rates-cell-label">Button {i + 1}</span>
                    <div className="rates-input-wrap">
                        <input
                            type="number"
                            className="settings-input rates-input"
                            min="0"
                            step={step}
                            value={v}
                            onChange={(e) => handleRateChange(setter, i, e.target.value)}
                        />
                        <span className="rates-suffix">{suffix}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderRates = () => (
        <div className="settings-panel">
            <div className="settings-panel-header">
                <h2>Rates Slider</h2>
            </div>
            <p className="settings-panel-desc">
                Customize the quick-select bet buttons above the action bar.
            </p>
            <hr className="settings-divider" />
            <span className="settings-field-label">Preflop — raise to (big blinds)</span>
            {renderRateRow(preflop, setPreflop, "BB", "0.5")}
            <span className="settings-field-label rates-second-label">Postflop — bet (% of pot)</span>
            {renderRateRow(postflop, setPostflop, "% Pot", "1")}
            {ratesStatus && (
                <p className={ratesStatus.type === "ok" ? "settings-msg-ok" : "settings-msg-error"}>
                    {ratesStatus.text}
                </p>
            )}
            <div className="settings-actions settings-actions-right">
                <button className="settings-action-btn" onClick={handleSaveRates}>
                    Save
                </button>
            </div>
        </div>
    );

    const renderSupport = () => (
        <div className="settings-panel">
            <div className="settings-panel-header">
                <h2>Support</h2>
            </div>
            <p className="settings-panel-desc">
                You can contact support at notreal@place.com
            </p>
            <hr className="settings-divider" />
            <div className="settings-actions settings-actions-right">
                <button className="settings-action-btn">
                    Contact Support
                </button>
            </div>
        </div>
    );

    const renderContent = () => {
        switch (activeTab) {
            case "Volume": return renderVolume();
            case "Rates Slider": return renderRates();
            case "Support": return renderSupport();
            case "Account":
            default: return renderAccount();
        }
    };

    return (
        <div className="settings-page">
            <div className="settings-window">
                <div className="settings-titlebar">
                    <span className="settings-titlebar-gear">&#9881;</span>
                    <h1>Settings</h1>
                </div>
                <div className="settings-body">
                    <nav className="settings-tabs">
                        {TABS.map((tab) => (
                            <button
                                key={tab}
                                className={`settings-tab ${activeTab === tab ? "active" : ""}`}
                                onClick={() => setActiveTab(tab)}
                            >
                                {tab}
                            </button>
                        ))}
                    </nav>
                    <div className="settings-content">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
